import { useEffect, useState } from "react";

/**
 * Runs `load` whenever `key` changes and keeps only the answer for the current key: a slow reply
 * for an older key never lands, and a new key reads as loading until its own reply arrives.
 * `key` must capture every input `load` reads; `null` turns the request off.
 */
export function useKeyedRequest<T>(key: string | null, load: (signal: AbortSignal) => Promise<T>) {
  const [retry, setRetry] = useState(0);
  const requestKey = key === null ? null : `${retry}:${key}`;
  const [state, setState] = useState<{
    key: string;
    data: T | null;
    error: unknown;
  } | null>(null);
  useEffect(() => {
    if (requestKey === null) return;
    const controller = new AbortController();
    load(controller.signal).then(
      data => {
        if (!controller.signal.aborted) setState({ key: requestKey, data, error: null });
      },
      (error: unknown) => {
        if (!controller.signal.aborted)
          setState({ key: requestKey, data: null, error: error ?? true });
      },
    );
    return () => controller.abort();
    // `key` stands in for everything `load` reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);
  const current = requestKey !== null && state?.key === requestKey ? state : null;
  return {
    data: current?.data ?? null,
    loading: requestKey !== null && current === null,
    failed: current !== null && current.error !== null,
    error: current?.error ?? null,
    reload: () => setRetry(value => value + 1),
  };
}
